<script>
import { AbcAccordion, AbcFloatingContainer } from "@ordbogen-npm/abc-dsk";
import { mapStores } from "pinia";
import { RouterLink } from "vue-router";

import { PACKAGE_GROUP_GYLDENDAL, PACKAGE_GROUP_ORDBOGEN } from "@/common/package-groups-types";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconHuman from "@/components/icons/IconHuman.vue";
import IconSettings from "@/components/icons/IconSettings.vue";
import IconSlidersVertical from "@/components/icons/IconSlidersVertical.vue";
import Accessibility from "@/components/settings/Accessibility.vue";
import Exam from "@/components/settings/ExamSettings.vue";
import Gyldendal from "@/components/settings/Gyldendal.vue";
import Institutions from "@/components/settings/InstitutionSetting.vue";
import Ordbogen from "@/components/settings/Ordbogen.vue";
import { useAuthStore, useSettingsStore } from "@/stores";

export default {
  name: "SettingsView",
  components: {
    AbcAccordion,
    AbcFloatingContainer,
    Accessibility,
    Gyldendal,
    IconHuman,
    IconSlidersVertical,
    IconSettings,
    IconEdit,
    Ordbogen,
    Exam,
    Institutions,
    RouterLink
  },
  computed: {
    ...mapStores(useAuthStore, useSettingsStore),

    component() {
      return ["ordbogen", "gyldendal", "accessibility", "exam", "institutions"].some(
        (page) => this.$route.params?.setting === page
      )
        ? this.$route.params.setting
        : this.activePackageGroup.id;
    },

    packageGroups() {
      return this.settingsStore.packageGroups;
    },

    activePackageGroup() {
      return this.settingsStore.preferredPackageGroup;
    },

    header() {
      return {
        type: "onlyHeader",
        userDisplayName: this.authStore.userDisplayName,
        class: this.authStore.user.className,
        institutionName: this.authStore.getPrimaryInstitutionName()
      };
    },

    items() {
      const ordbogen = {
        page: "/settings/ordbogen",
        component: "ordbogen",
        icon: "IconSlidersVertical",
        header: this.$t("settings.sidebar.pageNames.ordbogen")
      };

      const gyldendal = {
        page: "/settings/gyldendal",
        component: "gyldendal",
        icon: "IconSlidersVertical",
        header: this.$t("settings.sidebar.pageNames.gyldendal")
      };

      const accessibility = {
        page: "/settings/accessibility",
        component: "accessibility",
        icon: "IconHuman",
        header: this.$t("settings.sidebar.pageNames.accessibility")
      };

      const exam = {
        page: "/settings/exam",
        component: "exam",
        icon: "IconEdit",
        header: this.$t("settings.sidebar.pageNames.exam")
      };

      const institution = {
        page: "/settings/institutions",
        component: "institutions",
        icon: "IconSettings",
        header: this.$t("settings.sidebar.pageNames.institutions")
      };

      let dictionarySettings = [];
      if (this.packageGroups.length === 2) {
        dictionarySettings = [ordbogen, gyldendal];
      } else if (this.activePackageGroup.id === PACKAGE_GROUP_ORDBOGEN) {
        ordbogen.header = this.$t("settings.sidebar.pageNames.shared");
        dictionarySettings = [ordbogen];
      } else if (this.activePackageGroup.id === PACKAGE_GROUP_GYLDENDAL) {
        gyldendal.header = this.$t("settings.sidebar.pageNames.shared");
        dictionarySettings = [gyldendal];
      }

      return [this.header, ...dictionarySettings, accessibility, exam, institution];
    },
    accordionItems() {
      return this.items
        .filter((item) => item.type !== "onlyHeader")
        .map((item) => ({
          id: item.header,
          data: item
        }));
    }
  }
};
</script>

<template>
  <main class="container mb-5">
    <div class="row">
      <div class="col">
        <h1 class="mt-1 mb-2 mt-lg-2 mb-lg-3 text-secondary">{{ $t("settings.heading") }}</h1>
      </div>
    </div>
    <div class="row">
      <AbcFloatingContainer with-header>
        <div class="settings-accordion__header d-block d-lg-none">
          <p class="settings-accordion__preheading">{{ $t("settings.sidebar.user") }}</p>
          <div class="settings-accordion__heading mb-1">
            <p class="settings-accordion__heading-username">{{ header.userDisplayName }}</p>
            <p class="mb-0">{{ $t("settings.sidebar.class", { class: header.class }) }}</p>
          </div>
          <p class="settings-sidebar-header__institution mb-0">{{ header.institutionName }}</p>
        </div>
        <AbcAccordion
          id="settings"
          class="d-block d-lg-none"
          panel-class="settings-accordion__panel"
          :items="accordionItems"
        >
          <template #buttonContent="{ data, className }">
            <span :class="className" class="py-2">{{ data.header }}</span>
          </template>

          <template #panel="{ data }">
            <Component :is="data.component" />
          </template>
        </AbcAccordion>

        <div class="d-none d-lg-flex settings-view">
          <aside class="settings-sidebar">
            <div class="settings-sidebar-header">
              <p class="settings-sidebar-header__heading mb-0">{{ $t("settings.sidebar.user") }}</p>
              <div class="settings-sidebar-header__content mb-1">
                <p class="mb-0">{{ header.userDisplayName }}</p>
                <p class="settings-sidebar-header__class">
                  {{ $t("settings.sidebar.class", { class: header.class }) }}
                </p>
              </div>
              <p class="settings-sidebar-header__institution">{{ header.institutionName }}</p>
            </div>
            <ul class="settings-sidebar-navigation">
              <template v-for="(item, index) in items">
                <li v-if="item.type !== 'onlyHeader'" :key="index">
                  <RouterLink
                    :to="item.page"
                    class="settings-sidebar-navigation__link"
                    active-class="settings-sidebar-navigation__link--active"
                  >
                    <Component
                      :is="item.icon"
                      class="settings-sidebar-navigation__icon text-primary"
                    />
                    <span>{{ item.header }}</span>
                  </RouterLink>
                </li>
              </template>
            </ul>
          </aside>
          <section class="settings-component">
            <Component :is="component" />
          </section>
        </div>
      </AbcFloatingContainer>
    </div>
  </main>
</template>
